import { Component, inject, ChangeDetectionStrategy, computed, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';

import { TooltipModule } from 'primeng/tooltip';
import { MenuModule } from 'primeng/menu';
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';

import { QuoteStatus, Version } from 'src/api-service';
import { selectQuoteState, selectIsSubscriptionQuote } from 'src/app/store/pages/quote/quote.selectors';
import { selectVersion } from 'src/app/store/pages/quote/version/version.selectors';
import { selectHasDraftItems } from 'src/app/store/pages/quote/articles/articles.selectors';
import { QuoteVersionActions } from 'src/app/store/pages/quote/version/version.actions';
import { getQuoteStatusIcon, getQuoteStatusSeverity } from 'src/app/shared/util/quote-status.utils';
import { CreateVersionDialogComponent } from './dialogs/create-version-dialog/create-version-dialog.component';
import { KbdShortcutComponent } from 'src/app/shared/components/kbd-shortcut/kbd-shortcut.component';
import { KEYBOARD_SHORTCUTS } from 'src/app/shared/constants/keyboard-shortcuts';
import { Menu } from 'primeng/menu';
import { LocaleService } from 'src/app/shared/util/locale.service';

@Component({
    selector: 'app-version-actions',
    templateUrl: './version-actions.component.html',
    styleUrl: './version-actions.component.scss',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TooltipModule, MenuModule, ButtonModule, TranslateModule, KbdShortcutComponent],
})
export class VersionActionsComponent {
    public readonly SHORTCUTS = KEYBOARD_SHORTCUTS;
    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    private readonly store = inject(Store);
    private readonly dialogService = inject(DialogService);
    private readonly translate = inject(TranslateService);
    private readonly locale = inject(LocaleService);

    // =========================================================================
    // STORE SIGNALS
    // =========================================================================
    public readonly quote = toSignal(this.store.select(selectQuoteState));
    public readonly selectedVersion = toSignal(this.store.select(selectVersion));
    public readonly hasDraftItems = toSignal(this.store.select(selectHasDraftItems), { initialValue: false });
    public readonly isSubscription = toSignal(this.store.select(selectIsSubscriptionQuote), { initialValue: false });

    // =========================================================================
    // COMPUTED - Version Status
    // =========================================================================
    public readonly versionStatusIcon = computed(() => getQuoteStatusIcon(this.selectedVersion()?.status).icon);
    public readonly versionStatusColorClass = computed(() => getQuoteStatusIcon(this.selectedVersion()?.status).colorClass);
    public readonly versionStatusSeverity = computed(() => getQuoteStatusSeverity(this.selectedVersion()?.status));
    public readonly versionButtonClass = computed(() => `p-button-${this.versionStatusSeverity()}`);

    public readonly versionNumber = computed(() => {
        const versions = this.quote()?.versions ?? [];
        const selectedId = this.selectedVersion()?.id;
        const index = versions.findIndex(v => v.id === selectedId);
        return index >= 0 ? index + 1 : 1;
    });

    public readonly versionNameTruncated = computed(() => {
        const name = this.selectedVersion()?.name ?? '';
        return name.length > this.versionNameMaxLength ? name.substring(0, this.versionNameMaxLength) + '...' : name;
    });

    public readonly isVersionNameTruncated = computed(() => {
        const name = this.selectedVersion()?.name ?? '';
        return name.length > this.versionNameMaxLength;
    });

    // =========================================================================
    // COMPUTED - Lock state
    // =========================================================================
    public readonly isLocked = computed(() => this.quote()?.isLocked ?? false);
    public readonly lockMessage = computed(() => this.quote()?.lockMessage?.message ?? null);

    private readonly versionNameMaxLength = 20;

    // =========================================================================
    // COMPUTED - Version Menu Items
    // =========================================================================
    public readonly versionMenuItems = computed((): MenuItem[] => {
        this.locale.currentLang();
        const versions = this.quote()?.versions ?? [];
        const selectedId = this.selectedVersion()?.id;
        const hasDraft = this.isSubscription() ? false : this.hasDraftItems();
        const locked = this.isLocked();

        const versionItems: MenuItem[] = versions.map((version, index) => {
            const name = version.name ?? '';
            const isSelected = version.id === selectedId;
            const statusIcon = getQuoteStatusIcon(version.status);
            return {
                label: `v${index + 1} - ${name}`,
                icon: statusIcon.icon,
                iconClass: statusIcon.colorClass,
                badge: isSelected ? '✓' : undefined,
                badgeStyleClass: 'version-selected-badge',
                command: () => this.selectVersion(version),
                tooltipOptions: name.length > this.versionNameMaxLength ? { tooltipLabel: name } : undefined,
            };
        });

        const lockMsg = this.lockMessage();

        return [
            ...versionItems,
            { separator: true },
            {
                label: this.translate.instant('QUOTE.DETAIL.CREATE_NEW_VERSION'),
                icon: 'pi pi-plus',
                disabled: hasDraft || locked,
                tooltipOptions: lockMsg ? { tooltipLabel: this.translate.instant(lockMsg) } : undefined,
                command: () => this.openCreateVersionDialog(),
            },
        ];
    });

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================
    @ViewChild('versionMenu') private versionMenu?: Menu;

    public toggleVersionMenu(): void {
        const btn = document.querySelector('.yllw-quote-toolbar__version-btn') as HTMLElement;
        if (btn && this.versionMenu) {
            this.versionMenu.toggle({ currentTarget: btn } as any);
        }
    }

    public selectVersion(version: Version): void {
        this.store.dispatch(QuoteVersionActions.changeVersion({ version }));
    }

    public openCreateVersionDialog(): void {
        this.dialogService.open(CreateVersionDialogComponent, {
            header: this.translate.instant('CREATE_VERSION.TITLE'),
            closable: true,
            styleClass: 'dialog-container',
            draggable: false,
            dismissableMask: true,
        });
    }
}
